import React, { useEffect, useMemo, useState } from 'react';
import { LogOut, Moon, Sun } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  OrionAppShell,
  OrionButton,
  OrionThemeProvider,
  type OrionNavItem,
} from '@/OrionAppTemplate';
// Chat flotante = el MISMO ChatPane del chat completo (/chat) en modo «dock»:
// stream, @ / #, modelos, visuales, títulos y adjuntos. Ver assistant/activeThread.ts.
import { ChatPane } from './assistant/ChatPane';
import { useSharedDock, type DockReturnState } from './assistant/activeThread';
import { ModuleSwitcher } from './ModuleSwitcher';
import { OrionVoiceWidget } from './OrionVoiceWidget';
import { isQuetzalMode } from './QuetzalShell';
import { useBranding } from '@/context/BrandingContext';
import { useModules } from '@/context/ModulesContext';
import { usePermissions } from '@/context/PermissionsContext';
import { findItem, findModule, resolveItemLabel } from '@/modules';

import { API_BASE_URL } from '@/config';
import { getOrgInfo } from '@/lib/api';

/** Convert "#00e5ff" → "0, 229, 255" for use in rgba(var(--x-rgb), alpha) */
function hexToRgbTriplet(hex: string): string {
  const h = hex.replace('#', '');
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

interface LayoutProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
  onLogout: () => void;
  tokensOverrides?: Record<string, string>;
}

export function Layout({
  children,
  isAuthenticated,
  onLogout,
  tokensOverrides: propTokensOverrides,
}: LayoutProps) {
  const { orgName: brandOrgName, orgLogo: brandOrgLogo, agentPhoto, palette } = useBranding();
  const [orgName, setOrgName] = useState<string>('');
  const [orgLogo, setOrgLogo] = useState<string | null>(null);
  // Foto del agente per-org (Company.ai_agent_photo). Prioridad: el fetch propio
  // del Layout (/org/info, token-optional, SIEMPRE resuelve) → BrandingContext →
  // avatar por defecto. Así el FAB de chat usa el MISMO avatar que el login.
  const [navAgentPhoto, setNavAgentPhoto] = useState<string | null>(null);
  const agentImg = navAgentPhoto || agentPhoto || `${import.meta.env.BASE_URL}assets/orion.png`;
  const [theme, setTheme] = useState<'dark' | 'light'>(
    (localStorage.getItem('orion_theme') as 'dark' | 'light') || 'light',
  );
  // 🔁 Abierto/cerrado + hilo compartido con la pantalla completa (/chat).
  const { open: chatOpen, setOpen: setChatOpen, thread: dockThread } = useSharedDock();
  const [chatHasOpened, setChatHasOpened] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ moduleKey?: string; tab?: string }>();
  const { userRoles, rememberModule, archetype } = useModules();
  const { isCto, canPublish } = usePermissions();

  const activeModule = params.moduleKey ? findModule(params.moduleKey) : undefined;
  // 🚨 En el diagnóstico (org-scan) la conversación es EXCLUSIVA: sin FAB de
  // voz ni de agente flotante. El empleado sólo debe ver el chat central.
  const esDiagnostico = params.moduleKey === 'org-scan';
  // En la bandeja de Conversaciones el FAB tapa los mensajes (abajo-izquierda) y
  // además es redundante (ya estás viendo conversaciones) → se oculta.
  const esConversaciones = params.moduleKey === 'conversaciones';
  const activeTabKey = params.tab;

  // Persist the current module so the next sign-in lands here.
  useEffect(() => {
    if (params.moduleKey) rememberModule(params.moduleKey);
  }, [params.moduleKey, rememberModule]);

  // 🎨 Construir tokens CSS desde el tema del backend
  const tokensOverrides = useMemo(() => {
    const baseTokens = propTokensOverrides || {};
    if (palette) {
      return {
        ...baseTokens,
        '--acc1': palette.acc1,
        '--acc2': palette.acc2,
        '--acc3': palette.acc3,
        '--bg-dark': palette.bg_dark,
        '--bg-light': palette.bg_light,
        '--text-dark': palette.text_dark,
        '--text-light': palette.text_light,
        // Alias para componentes que usan --accent
        '--accent': palette.acc1,
      };
    }
    return baseTokens;
  }, [palette, propTokensOverrides]);

  // Lazy load del chat para ahorrar ancho de banda al inicio
  useEffect(() => {
    if (chatOpen && !chatHasOpened) {
      setChatHasOpened(true);
    }
  }, [chatOpen, chatHasOpened]);

  // Esc minimiza (si no lo usó antes el menú de @ / # o el renombrado del título).
  useEffect(() => {
    if (!chatOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) setChatOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatOpen, setChatOpen]);

  // «Ampliar»: la misma conversación en /chat; su «Minimizar» regresa aquí.
  const expandChat = (id: string | null) => {
    setChatOpen(false);
    const state: DockReturnState = { fromDock: location.pathname + location.search };
    navigate(id ? `/chat?hilo=${encodeURIComponent(id)}` : '/chat', { state });
  };
  const dockHeaders = useMemo(() => {
    const org = localStorage.getItem('org_id');
    return org ? { 'x-org-id': org } : {};
  }, []);

  const API_BASE = API_BASE_URL.replace(/\/$/, '');

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const info = await getOrgInfo();
        setOrgName((info as any)?.name || 'Dashboard');
        setOrgLogo((info as any)?.logo || null);
        setNavAgentPhoto((info as any)?.agent_photo || null);
      } catch {
        /* noop */
      }
    })();
  }, [isAuthenticated]);

  const displayName = orgName || brandOrgName || 'Dashboard';
  const displayLogo = orgLogo ?? brandOrgLogo;

  const paletteTokens: Record<string, string> = useMemo(() => {
    const tokens: Record<string, string> = {};
    if (palette) {
      tokens['--acc1'] = palette.acc1;
      tokens['--acc2'] = palette.acc2;
      tokens['--acc3'] = palette.acc3;
      tokens['--acc1-rgb'] = hexToRgbTriplet(palette.acc1);
      tokens['--acc2-rgb'] = hexToRgbTriplet(palette.acc2);
      tokens['--acc3-rgb'] = hexToRgbTriplet(palette.acc3);
      tokens['--bg-dark'] = palette.bg_dark;
      tokens['--bg-light'] = palette.bg_light;
      tokens['--bg-dark-rgb'] = hexToRgbTriplet(palette.bg_dark);
      tokens['--bg-light-rgb'] = hexToRgbTriplet(palette.bg_light);
      tokens['--text-dark'] = palette.text_dark;
      tokens['--text-light'] = palette.text_light;
    }
    return { ...tokens, ...tokensOverrides };
  }, [palette, tokensOverrides]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('orion_theme', theme);
  }, [theme]);

  // CTO bypass (ticket 068) — applies to hiding dev-only items from regular users.
  const hasDevAccess = !!(userRoles && (userRoles.includes('cto') || userRoles.includes('developer')));
  const isCtoRole = !!(userRoles && userRoles.includes('cto')) || isCto;
  const isAdminRole = !!(userRoles && userRoles.some((r) => ['admin', 'cto', 'developer'].includes(r)));
  const isPartner = !isAdminRole && !!(userRoles && userRoles.some((r) => ['vendedor', 'socio', 'partner'].includes(r)));

  // Sidebar comes from the active module's items in the moduleRegistry.
  // Items that declare `requirePublish` are hidden from keys without that scope.
  // `devOnly` items stay hidden from non-dev users.
  const nav: OrionNavItem[] = useMemo(() => {
    if (!activeModule) return [];
    return activeModule.sidebarItems
      .filter((item) => {
        if (item.requireAdmin && !isAdminRole && !hasDevAccess) return false;
        if (!isCtoRole) {
          if (item.devOnly && !hasDevAccess) return false;
          if (item.requirePublish && !canPublish) return false;
        }
        return true;
      })
      .map((item) => {
        const label = (isPartner && item.partnerLabel) || resolveItemLabel(activeModule, item, archetype);
        return {
          key: `${activeModule.key}.${item.tab}`,
          label: item.comingSoon ? `${label} · próximamente` : label,
          icon: item.icon,
          current: activeTabKey === item.tab,
          onClick: () => navigate(`/m/${activeModule.key}/${item.tab}`),
        };
      });
  }, [activeModule, activeTabKey, archetype, canPublish, hasDevAccess, isCtoRole, isAdminRole, isPartner, navigate]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  if (!isAuthenticated) {
    return (
      <OrionThemeProvider tokensOverrides={paletteTokens}>
        <div className={`o-bg ${theme === 'dark' ? 'o-bg--dark' : 'o-bg--light'}`}>
          <div className="o-ambient" aria-hidden />
          <div className="o-wrap p-5">{children}</div>
        </div>
      </OrionThemeProvider>
    );
  }

  // Brand block: logo + ModuleSwitcher. Replaces the old hardcoded
  // "Dashboard Viajes" org-name text. `overflow-visible` so the switcher
  // dropdown can escape the header and overlay the nav tabs below.
  const brandNode = (
    <div className="flex items-center gap-3 min-w-0 overflow-visible">
      <BrandLogo url={displayLogo} alt={`${displayName} logo`} />
      <ModuleSwitcher activeModuleKey={activeModule?.key} />
    </div>
  );

  return (
    <OrionThemeProvider tokensOverrides={paletteTokens}>
      <div className={`o-bg ${theme === 'dark' ? 'o-bg--dark' : 'o-bg--light'} min-h-[100svh] flex flex-col`}>
        <div className="o-ambient" aria-hidden />

        <OrionAppShell
          brand={{ name: displayName, logo: brandNode }}
          nav={nav}
          onToggleTheme={toggleTheme}
          rightActions={
            <div className="flex items-center gap-2">
              <OrionButton aria-label="Cambiar tema" title="Cambiar tema" onClick={toggleTheme} className="o-btn-ghost">
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </OrionButton>
              <OrionButton aria-label="Salir" onClick={onLogout} className="o-btn-ghost">
                <LogOut size={16} />
                <span className="ml-1 hidden sm:inline">Salir</span>
              </OrionButton>
            </div>
          }
        >
          <div className="o-page p-6">{children}</div>
        </OrionAppShell>

        {/* FAB voz (derecha) — universal. Skip si QuetzalMode ya monta su
            propio VoiceWidget (evita dos FABs duplicados en demo Quetzal).
            Gate por org: la voz realtime NO es soberana (OpenAI Realtime), así
            que se oculta para orgs `government` (municipio y futuras
            dependencias corren solo sobre el modelo soberano U-CogNet). Otras
            orgs no cambian. El override fino per-org llega con la capa
            ui_settings (ver Configuración). */}
        {!isQuetzalMode() && !esDiagnostico && archetype !== 'government' && <OrionVoiceWidget />}

        {/* FAB Chat (izquierda) — oculto en diagnóstico y en Conversaciones (tapa los mensajes) */}
        {!esDiagnostico && !esConversaciones && <button
          type="button"
          className={`o-chat-fab o-chat-fab--left ${chatOpen ? 'opacity-0 pointer-events-none' : ''}`}
          aria-label={`Chatear con ${displayName}`}
          onClick={() => setChatOpen(true)}
        >
          <span className="o-fab-ring" aria-hidden="true" />
          <img src={agentImg} alt={displayName} className="o-fab-img" />
        </button>}

        {/* Overlay + Dock chat */}
        <div
          className={`o-chat-overlay ${chatOpen ? 'is-open' : ''}`}
          aria-hidden={!chatOpen}
          onClick={() => {
            setChatOpen(false);
          }}
          style={{ pointerEvents: chatOpen ? 'auto' : 'none', zIndex: chatOpen ? 111 : -1 }}
        />
        <section
          className={`o-chat-dock ${chatOpen ? 'is-open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={`Chat con ${displayName}`}
          aria-hidden={!chatOpen}
          style={{ pointerEvents: chatOpen ? 'auto' : 'none', zIndex: chatOpen ? 120 : -1 }}
        >
          <div className="o-chat-body">
            {chatHasOpened && (
              <ChatPane
                key={dockThread ?? 'base'}
                variant="dock"
                threadId={dockThread}
                webChatHeaders={dockHeaders}
                shareThread
                mentions
                modelPicker
                dockTitle={displayName}
                dockLeading={<img src={agentImg} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover object-[50%_20%]" />}
                onExpand={expandChat}
                onMinimize={() => setChatOpen(false)}
              />
            )}
          </div>
        </section>
      </div>
    </OrionThemeProvider>
  );
}

// Re-export for callers that still check the tuple shape.
export function resolveActiveRoute(moduleKey?: string, tab?: string) {
  if (!moduleKey || !tab) return null;
  return findItem(moduleKey, tab);
}

/** Renders the org logo with a silent fallback. When the URL is absent
 *  or the image fails to load, the component collapses to `null` instead
 *  of showing the broken-image alt text next to the ModuleSwitcher. */
function BrandLogo({ url, alt }: { url: string | null | undefined; alt: string }) {
  const [failed, setFailed] = useState(false);
  // Reset the failure flag if the URL changes (e.g. user switches org).
  useEffect(() => { setFailed(false); }, [url]);
  if (!url || failed) return null;
  return (
    <div className="o-logo-glass shrink-0">
      <img
        src={url}
        alt={alt}
        className="h-[22px] w-auto"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

export default Layout;
